import api from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';

/**
 * The internal team directory — colleagues an agent can see and dial.
 *
 * Deliberately NOT the extensions API. That one is admin-only and returns the
 * seat entity including its SIP password; this is a read-only projection any
 * agent may fetch, which is why agent-to-agent calling can exist in the portal
 * at all.
 */
export interface DirectoryEntry {
  extension: string;
  displayName: string;
  followMeEnabled: boolean;
  /** This seat belongs to the signed-in user — offered for reference, not dialling. */
  self: boolean;
  /**
   * Device state from the switch: "Not in use", "In use", "Unavailable"…
   * NULL when the switch could not be reached — rendered as unknown, never as
   * available, since a green dot invites a call that rings out.
   */
  presence: string | null;
}

/** This API wraps some payloads in `data.data` and returns others bare. */
function unwrap<T>(res: any): T {
  return (res?.data?.data ?? res?.data) as T;
}

class DirectoryService {
  /** Every dialable colleague in the caller's own organization. */
  async list(): Promise<DirectoryEntry[]> {
    const res = await api.get(API_ENDPOINTS.DIRECTORY);
    const body = unwrap<any>(res);
    return (body?.entries ?? []) as DirectoryEntry[];
  }

  /**
   * Resolve one extension to a colleague, or null if it is not one.
   *
   * A 404 is the normal answer for an outside caller, not a failure, so it
   * resolves to null rather than throwing — the caller of this is a pre-answer
   * lookup that must never disrupt a ringing call.
   */
  async lookup(extension: string): Promise<DirectoryEntry | null> {
    try {
      const res = await api.get(API_ENDPOINTS.DIRECTORY_LOOKUP(encodeURIComponent(extension)));
      return unwrap<DirectoryEntry>(res) ?? null;
    } catch {
      return null;
    }
  }
}

export const directoryService = new DirectoryService();
